import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, RESORT, WHATSAPP_URL } from "@/lib/data";
import { Instagram, Facebook, Send, MessageCircle } from "lucide-react";

export default function Footer() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        try {
            setBusy(true);
            const { data } = await api.post("/newsletter", { email });
            toast.success(data.message || "You're on the list.");
            setEmail("");
        } catch (err) {
            toast.error("Could not subscribe. Try again shortly.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <footer data-testid="site-footer" className="bg-navy text-ivory mt-20 relative overflow-hidden grain">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-4 gap-12">
                <div className="md:col-span-2">
                    <div className="font-display text-3xl">
                        Serenity <span className="text-gold">Cove</span>
                    </div>
                    <p className="mt-4 text-ivory/70 max-w-md leading-relaxed">
                        A boutique escape in Ocho Rios, Jamaica — with quick access to the beach. Garden suites, quiet luxury,
                        and curated island discovery.
                    </p>
                    <form onSubmit={submit} className="mt-8 flex items-center gap-2 max-w-md">
                        <input
                            data-testid="newsletter-email-input"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Your email address"
                            className="flex-1 bg-transparent border-b border-ivory/40 focus:border-gold outline-none py-3 text-sm placeholder:text-ivory/50"
                        />
                        <button
                            data-testid="newsletter-submit-button"
                            disabled={busy}
                            className="btn-gold px-4 py-2.5 rounded-full text-sm flex items-center gap-2 disabled:opacity-60"
                        >
                            <Send size={14} /> Join
                        </button>
                    </form>
                </div>
                <div>
                    <div className="text-xs uppercase tracking-[0.24em] text-gold mb-4">Visit</div>
                    <p className="text-ivory/80 text-sm leading-relaxed">{RESORT.address}</p>
                    <a href="tel:+18766347439" className="text-ivory/80 text-sm mt-3 block hover:text-gold">{RESORT.phone}</a>
                    <a href={`mailto:${RESORT.email}`} className="text-ivory/80 text-sm hover:text-gold">
                        {RESORT.email}
                    </a>
                    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link" className="mt-3 inline-flex items-center gap-2 text-ivory/80 text-sm hover:text-gold">
                        <MessageCircle size={14} className="text-gold" /> Chat on WhatsApp
                    </a>
                </div>
                <div>
                    <div className="text-xs uppercase tracking-[0.24em] text-gold mb-4">Explore</div>
                    <ul className="space-y-2 text-sm">
                        {[
                            ["/rooms", "Rooms & Suites"],
                            ["/amenities", "Amenities"],
                            ["/gallery", "Gallery"],
                            ["/experiences", "Experiences"],
                            ["/local-area", "Local Area"],
                            ["/offers", "Offers"],
                            ["/contact", "Contact"],
                        ].map(([to, l]) => (
                            <li key={to}>
                                <Link to={to} className="text-ivory/70 hover:text-gold transition-colors">
                                    {l}
                                </Link>
                            </li>
                        ))}
                    </ul>
                    <div className="flex items-center gap-4 mt-6">
                        <a href="#" aria-label="Instagram" className="text-ivory/70 hover:text-gold"><Instagram size={18} /></a>
                        <a href="#" aria-label="Facebook" className="text-ivory/70 hover:text-gold"><Facebook size={18} /></a>
                    </div>
                </div>
            </div>
            <div className="border-t border-ivory/10">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-ivory/50">
                    <p>© {new Date().getFullYear()} Serenity Cove Resorts · Ocho Rios, Jamaica</p>
                    <p className="font-serif-italic text-gold">where sea light meets soft linen</p>
                </div>
            </div>
        </footer>
    );
}
